import type { ReactNode } from "react";
import { Link } from "react-router";

/** Where privacy requests go; other builds may set VITE_PRIVACY_CONTACT at build time. */
export const PRIVACY_CONTACT: string = import.meta.env.VITE_PRIVACY_CONTACT || "privacy@ten20.live";

const UPDATED = "21 October 2026";

/** A public, signed-out page with a readable column of text. */
export function LegalLayout({ title, children }: { title: string; children: ReactNode }) {
  return (
    <main className="legal-page">
      <article className="card legal-card">
        <p className="brand">
          <Link to="/">ten20</Link>
        </p>
        <h1>{title}</h1>
        {children}
        <p className="hint">
          <Link to="/privacy">Privacy policy</Link> · <Link to="/terms">Chat terms</Link> ·{" "}
          <Link to="/delete-account">Deleting your account</Link>
        </p>
      </article>
    </main>
  );
}

export function ContactLink() {
  return <a href={`mailto:${PRIVACY_CONTACT}`}>{PRIVACY_CONTACT}</a>;
}

/**
 * The privacy policy linked from the Play and App Store listings, the apps'
 * About sections and the sign-in pages. Keep it in step with what the server
 * and apps actually do (goals/whereat-mvp/DESIGN.md, "P6 privacy policy";
 * the iPhone app's inventory in docs/app-store.md).
 */
export function PrivacyPage() {
  return (
    <LegalLayout title="Privacy policy">
      <p className="muted">Last updated {UPDATED}</p>

      <p>
        ten20 lets family and friends share their location and chat with each other. This policy explains what the ten20
        service at app.ten20.live collects, why, who can see it and how long it is kept. It covers the web app, the
        Android and iPhone apps and the Home Assistant integration when they are connected to app.ten20.live, and the
        early access list on ten20.live.
      </p>

      <h2>The short version</h2>
      <ul>
        <li>Your location is shown only to the people in your circles, and only while you are sharing.</li>
        <li>
          The Android and iPhone apps collect your location in the background, even when the app is closed or not in use
          (on an iPhone, when you allow location &quot;Always&quot;).
        </li>
        <li>Location history is deleted automatically after 30 days.</li>
        <li>
          Your messages are seen by the people in that chat (a circle&apos;s members, or the other person in a direct
          chat). They are stored on the server, not end-to-end encrypted, and read by us only when reported.
        </li>
        <li>
          No ads, no third-party analytics or tracking tools, and your data is never sold. We count how ten20 is used on
          our own server, as totals that never single you out.
        </li>
        <li>
          You can pause sharing, share only an approximate area, download your data or delete your account at any time.
          A pause hides that period for good: after you resume, a circle never sees what was recorded while it was
          paused.
        </li>
        <li>
          In a family circle you can agree to be supervised: guardians you see by name can pause your sharing with that
          circle and approve some of your actions first. Nobody is supervised without accepting it, you always see it,
          you&apos;re told about everything a guardian does, and you can stop it on your own after a notice period.
        </li>
        <li>
          In a friends circle you share an approximate area (about 2 km) until you choose to share your exact location.
          Events share locations only while the event is on (what was shared then stays visible to its members), and are
          deleted 30 days after they end.
        </li>
        <li>
          While an SOS you send is open, your family circles (and the friends circles and live events it reaches) see
          your exact location, even if you paused sharing. An SOS does not contact emergency services.
        </li>
      </ul>

      <h2>What we collect</h2>
      <h3>Your account</h3>
      <p>
        Your email address, your name, an optional profile photo, your password (stored only as a salted hash) and the
        public keys of any passkeys you add. We use your email address to sign you in and to confirm account deletion;
        we do not send marketing email.
      </p>
      <p>
        Your profile photo is stored by ten20 on our own server, which re-encodes it as a square in two sizes, removing
        its location and camera details. Everyone who sees your name in ten20 (your circles, the people you chat with,
        and their notifications) sees it with your photo, loaded from our server only.
      </p>
      <h3>Your location (Android and iPhone apps)</h3>
      <p>
        While sharing is on, the Android and iPhone apps collect your location and send it to the server:{" "}
        <strong>
          this happens in the background, even when the app is closed or not in use, so that your circles stay up to
          date
        </strong>
        . Each location fix contains coordinates, accuracy, altitude, speed and direction when the phone provides them,
        the time, your battery level and whether the phone is charging, whether you seem to be still, walking or
        driving, and why the fix was taken (for example a regular update, a circle member asking for one, or arriving at
        a place). Fixes wait on the phone until they are uploaded. The app also reports whether the phone&apos;s
        Location setting is switched on and which location permission it has. With the physical activity permission (on
        an iPhone, Motion &amp; Fitness) the phone tells whether you are still, walking or driving, so the app reports
        less often while you are still; only that mode, and since when, is sent with your location.
      </p>
      <p>
        On an iPhone, the app shares in the background only if you allow location &quot;Always&quot;. With &quot;While
        Using the App&quot; or &quot;Allow Once&quot; it collects your location only while ten20 is on the screen, and
        your circles see that your location is limited. Without precise location iOS gives the app only an approximate
        position, which your circles see as such, and places may miss you arriving or leaving. To keep working in the
        background while you are still, the iPhone app also asks iOS for low-accuracy positions, which stay on the phone
        and are never sent.
      </p>
      <p>The web app and the Home Assistant integration do not collect your location.</p>
      <h3>Daily summaries</h3>
      <p>
        From your location history the server works out a summary for each day: distance driven and walked, the number
        and length of drives, your top sustained driving speed and which saved places you stopped at. A summary holds no
        coordinates or times of day, and uses your account&apos;s time zone to decide where a day begins: the Android
        and iPhone apps save your phone&apos;s time zone, and the web app your browser&apos;s if none is set yet (UTC
        until then). Members of your circles can see your weekly driving totals and drives.
      </p>
      <h3>SOS alerts</h3>
      <p>
        When you send an SOS, the server records when it started and ended, whether you or the 24-hour limit ended it,
        who in your circles responded (&quot;I&apos;m on it&quot;) and when, and the location sent with it, if any.
        While the alert is open the phone app sends your location often (on Android about every 15 seconds) for up to an
        hour.
      </p>
      <h3>Emergency contacts</h3>
      <p>
        If you add emergency contacts (on servers that can send email), the server stores each one&apos;s name and email
        address, when we emailed them and whether they accepted. We email a new contact once to ask them to accept,
        naming you and your email address, and email them again only if you ask. When you send an SOS, each contact who
        accepted gets an email with a private link to a page showing your name, when the alert started and your
        location, updated until the alert ends. The link stops working an hour after the alert ends (at most 24 hours
        after it started), or as soon as you remove the contact.
      </p>
      <h3>Check-ins</h3>
      <p>
        When you check in to a circle, the server records when, your message (if any) and the location your phone or
        browser read for it. That location is stored only with the check-in, not in your location history.
      </p>
      <h3>Chats</h3>
      <p>
        The messages you send (with any reply, mentions of other members, and when you edited or unsent them), the
        photos, videos and files you attach to them (the web and Android apps shrink photos and remove the location from
        videos before uploading them, and the Android app converts videos to 720p; photos you take with the camera from
        the Android app are kept only until they are uploaded; photos are re-encoded on our server too, which removes
        their camera and location data, and get a small preview; a video is stored as uploaded with a preview frame; a
        file is stored as it is and keeps its name and type), your reactions, how far you have read in each chat, your
        settings for each chat (notifications, mute, history you cleared), the people you blocked, whether read receipts
        and typing are on, and which version of the <Link to="/terms">chat terms</Link> you agreed to. When you report a
        message or a person we keep the report: who it is about, your reason and note, for a message a copy of it (its
        text, sender and times), and for a person the profile photo they had then, kept until the report is closed.
        &quot;Typing…&quot; signals and read receipts are passed on as they happen and not stored beyond your read
        position.
      </p>
      <p>
        Location messages you send: a pin (your current location, which your phone or browser reads for it and which is
        stored only with the message, or a point you chose or a circle place), a meeting point (with its name and time)
        or a live ETA to a destination. While an ETA is live the server works out your expected arrival time and the
        distance left from the locations it already receives and shows those (never your position) in the chat, until
        you arrive, stop it or 3 hours pass; it ends early if you pause sharing or share only an area. Which message is
        pinned in each chat, and who pinned it, is stored too.
      </p>
      <p>
        Voice messages you record: the sound clip as your browser or phone recorded it (stored as uploaded), its length
        and a short loudness outline your device measured to draw it. The microphone is used only while you record one
        (while you hold the microphone button, or from a tap until you send or cancel); nothing is recorded otherwise.
      </p>
      <h3>Circles, places and alerts</h3>
      <p>
        The circles you belong to, their kind (family, friends or event) and your role in them, whether your SOS reaches
        each friends circle, invites, the places your circles save (name, centre, radius and an optional address), the
        arrivals and departures detected at those places, your alert settings, and the names of any integration tokens
        you create (the token secret itself is stored only as a hash). For an event: who organised it, when it starts
        and ends and in which time zone, and its description and meeting point, if given.
      </p>
      <h3>Supervision</h3>
      <p>
        If you supervise someone or are supervised: the controls (for pausing, approximate sharing, leaving, deleting
        the account and joining or creating circles, whether you decide alone, guardians are told, or a guardian
        approves first), the guardians, offers and supervised invites, when supervision started and any notice to end
        it, requests and what was asked (for a join, the invite; for a new circle, its name and kind), each
        guardian&apos;s decision and note, and an activity log of changes, guardian actions, requests and notices. For
        each pause of a circle: when it started and ended, and who set it (you or a guardian).
      </p>
      <h3>Devices and sign-ins</h3>
      <p>
        For each phone or browser you sign in on: its name (such as &quot;Pixel 9&quot;; for an iPhone its model, such
        as &quot;iPhone SE&quot;, never the name you gave it), platform, app version (and, for the Android and iPhone
        apps, the Android or iOS version), when it signed in and was last used, the push address that lets us send it
        alerts and, for the Android and iPhone apps, whether the phone&apos;s Location setting is on and which location
        permission the app has (precise or approximate, all the time or only while the app is open).
      </p>
      <h3>How ten20 is used</h3>
      <p>
        For each day (in UTC) you use ten20, the server records that you were active that day and with which apps
        (Android, iPhone, web, Home Assistant or another integration), not when, how often or what you did. From those
        records and the rest of its database it works out daily totals, such as sign-ups, people active, circles by kind
        and size, how many messages (by kind), photos, videos, check-ins and alerts were sent and how much space
        attachments take, how many locations were uploaded and by how many people (counting those hidden from your
        circles by a pause or an approximate area too, as a number only), app, Android and iOS versions and how many
        phones have location permission. Totals are numbers only: they hold no names, email addresses or account ids.
      </p>
      <p>
        Once a day the server also counts the people active in the last 30 days by the area their latest location (under
        30 days old) falls in: country, region, and squares of about 5 km at the finest. It counts only a location your
        circles can see: a location hidden by a pause is left out (even during an SOS), and while you share an
        approximate area, its centre is counted instead. Only the number of people per area is stored, never who they
        are or which area you were counted in, and an area with fewer than 5 people is never shown, nor can its count be
        worked out by subtracting from the total of the larger area it lies in.
      </p>
      <p>
        All of this is done on our own server. The apps, websites and admin console contain no third-party analytics,
        tracking, advertising or crash reporting tools.
      </p>
      <h3>Technical data</h3>
      <p>
        Your IP address is used briefly, in memory, to limit repeated sign-in and invite attempts. Our web server keeps
        access logs (IP address, time, requested page and browser type) for up to two weeks to keep the service secure
        and working. Application logs never contain coordinates, passwords or tokens.
      </p>

      <h2>How we use it</h2>
      <p>
        Only to run ten20: to show your circles where you are, your battery level and when you were last seen; to detect
        when you arrive at or leave a place and send the alerts your circle members have asked for (arrivals,
        departures, low battery, a phone that has gone quiet or whose location is turned off or limited, and driving
        over a speed they chose); to show your recent location history; to fetch a fresh location when a circle member
        asks; to deliver your messages and notify the people you send them to; to handle reports of abuse; to alert your
        circles (and the emergency contacts you added) when you send an SOS; to keep accounts secure; and to understand,
        as the totals above, how ten20 is used so we can keep it running and improve it. The legal basis is providing
        the service you asked for and our legitimate interest in keeping it secure and understanding how it is used.
      </p>

      <h2>Who can see it</h2>
      <ul>
        <li>
          <strong>Members of your circles</strong> see your latest location, battery, current place, arrivals and
          departures and your location history since you joined that circle, whether your phone&apos;s Location is off
          or limited (not while you pause sharing), and the check-ins you send to it. Leaving a circle stops this and
          deletes your check-ins there.
        </li>
        <li>
          <strong>People in a chat</strong> see the messages and reactions posted there: every member of a circle sees
          its chat from when they joined (not earlier messages), and a direct chat is between the two of you. Your
          messages stay in a circle&apos;s chat after you leave it, but you can no longer read it. Others see your read
          position and when you are typing only if you both have read receipts and typing switched on. Circle admins can
          remove messages from their circle&apos;s chat.
        </li>
        <li>
          <strong>Notifications</strong> of a new message show its sender (with their profile photo), the chat&apos;s
          name and the start of the message (up to 100 characters) on the recipient&apos;s browser or computer, so
          anyone who can see their screen may see it. On Android the notification is built on the phone (see Firebase
          below) and shows the recent unread messages in full, so anyone who can see the phone&apos;s screen (or,
          depending on its settings, its lock screen) may see them; you can reply or mark them read from it. The Android
          app keeps a copy of your chats on the phone until you sign out.
        </li>
        <li>
          <strong>We (the operator)</strong> see a message when someone reports it, with the report, which may also be
          emailed to our moderation address. See the <Link to="/terms">chat terms</Link> for what we may do then.
        </li>
        <li>
          <strong>Our staff</strong> sign in to a separate admin console with their own passkey. Staff who handle
          accounts and moderation can look up an account and see its details: email address, name, profile photo, when
          it was created and last active, time zone, whether it is suspended, whether sharing is paused (not where you
          are), its circles (name, kind, members and their roles), its devices (name, platform, app and Android or iOS
          version, when added and last seen, whether signed in, whether alerts can reach them, and the phone&apos;s
          Location setting and permission), how many sign-ins it has, the names of its integration tokens, reports by or
          about it, and whether it is supervised. They can remove its profile photo, suspend an account, sign it out
          everywhere and, at your request, delete it. They never see your locations, history, places, check-ins,
          messages (except reported ones), photos you send in chats, voice messages or emergency contacts. Staff who
          only read the statistics see totals, never names or email addresses. Every search, look at an account or
          circle and action is recorded in an audit log saying who did it and when.
        </li>
        <li>
          <strong>Friends circles</strong>: when you join one (or create one) you start sharing an approximate area of
          about 2 km with it instead of your exact location (as below), until you turn that off for the circle. Your SOS
          reaches a friends circle only if you turn on &quot;Send my SOS here&quot; for it, and its members get alerts
          about your check-ins only if they turn them on. If an admin turns a family circle into a friends circle (or
          back), nobody&apos;s sharing changes, and every member&apos;s SOS reaches it until they turn that off.
        </li>
        <li>
          <strong>Events</strong> are circles with a start and an end. Their members see your location, battery, current
          place and check-ins only while the event is on, never before or after it, and never your weekly driving.
          Places saved in the event are visible to its members at any time. Once it has ended they can still look at
          your history from the event (only what was recorded between its start and end) until the event is deleted.
          Your SOS reaches an event only while it is on. Integrations can&apos;t be connected to events. After the event
          its chat stays open for 7 days, then becomes read-only; 30 days after the end the event is deleted with its
          chat, places, invites and memberships, after a warning a day before.
        </li>
        <li>
          <strong>While you share an approximate area</strong> with a circle, its members (and the integrations they set
          up) see an area with a radius of 1, 2, 5 or 25 km (your choice) around a random point near you, not where you
          are, until it ends. They still see your battery and when your phone last contacted the server, but not your
          location, place, arrivals and departures, check-ins, drives, or the history recorded while it was on, then or
          later. Your phone keeps sending locations, which are stored and deleted like the rest of your history. Sending
          an SOS ends approximate sharing.
        </li>
        <li>
          <strong>Supervision</strong> (family circles, only once you accept it, through a supervised invite or an offer
          that shows the controls and guardians first): the guardians, admins named in it, see the controls, your
          sharing with their circle (paused, by whom and until when, an approximate area, and whether you paused or
          share an area everywhere, without where you are), your requests with a short summary (for a join, the
          circle&apos;s name, kind and size, never the invite; for a new circle, its name and kind) and their notes, a
          notice when you take an action they are told about, when you start ending supervision, and when you have had
          no signed-in device for 5 minutes. By default they also get alerts when your battery is low, your phone&apos;s
          location is off or your location is out of date, unless they turn those off. Guardians can pause your sharing
          with their circle (for 1, 3, 6 or 24 hours or until resumed), resume it, share an approximate area for you
          there, change or end the supervision, and approve or decline your requests, which then run as your own action;
          you are told every time and you can always resume or end an area yourself. They never read your chats, see
          your other circles or which other circles supervise you, change your sharing anywhere else or touch your
          devices or sign-ins. You can stop supervision on your own: it ends after a notice period (7 days unless the
          server sets 1 to 30), which guardians are told about and can&apos;t change. SOS alerts and emergency contacts
          work exactly as without supervision.
        </li>
        <li>
          <strong>During an SOS</strong>, members of the circles it reaches (your family circles, the friends circles
          where you turned on &quot;Send my SOS here&quot; and events that are on; and the integrations they set up) see
          your exact location, battery and location history from when the alert started (up to 10 minutes earlier for
          the location sent with it), and who is responding, even if you paused sharing. When the alert ends, your pause
          applies again and hides all of it once more; the locations sent during the alert are kept like the rest of
          your history and deleted after 30 days. An SOS is a best-effort alert to the people in your circles: it is{" "}
          <strong>not a replacement for calling emergency services</strong>, and delivery depends on their phones,
          networks and notification settings.
        </li>
        <li>
          <strong>Your emergency contacts</strong>, during an SOS: anyone with the link emailed to them sees your name,
          when the alert started and your location from the alert on, until it ends. Email passes through our mail
          provider and theirs.
        </li>
        <li>
          <strong>Integrations a circle member sets up</strong>, such as Home Assistant, see what that member can see in
          that circle, until the member revokes the token or leaves the circle.
        </li>
        <li>
          <strong>Service providers</strong> who process data for us:
          <ul>
            <li>Akamai (Linode) hosts the server and database.</li>
            <li>
              Google Firebase Cloud Messaging delivers alerts and &quot;update your location&quot; requests to Android
              phones. Alerts (SOS alerts included) carry the circle, member and place names, battery level, driving
              speed, check-in messages and times, but never coordinates. New-message notifications carry only ids (no
              message text, names or previews); the app then fetches the message from our server itself.
            </li>
            <li>
              Apple Push Notification service delivers alerts and &quot;update your location&quot; requests to iPhones,
              once the iPhone app can receive notifications (test versions of the iPhone app don&apos;t yet). Apple
              handles their content, which is encrypted on its way to and from Apple. They carry the same as on Android
              (names, battery level, driving speed, check-in messages and times, never coordinates) with a short
              notification text made from them; new-message notifications carry only ids.
            </li>
            <li>
              Your browser&apos;s push service (for example Google, Mozilla or Apple) delivers web alerts and
              new-message notifications; their content (including message previews) is end-to-end encrypted.
            </li>
            <li>
              OpenFreeMap serves the map images, and Esri serves the satellite images when you switch satellite view on.
              Your browser or phone requests the map area you are viewing directly from them, so they see your IP
              address and that area, but no account details or members&apos; coordinates (though the area may show
              roughly where someone you are looking at is).
            </li>
            <li>
              To show members&apos; street addresses, the server may look up the latest location, rounded to about 11
              metres, with a Nominatim address service such as OpenStreetMap&apos;s. It looks up a place&apos;s position
              the same way to suggest names and an address while you create or edit it. No account details are sent.
            </li>
            <li>
              While you share a live ETA in a chat, the server may ask a routing service it is set up with (OSRM) for
              the driving time from your latest location to the destination, at most every 30 seconds until the ETA
              ends. No account details are sent.
            </li>
            <li>
              &quot;Open in Maps&quot; on a location in a chat hands that point and its name to your maps app (Apple
              Maps, your phone&apos;s maps app or OpenStreetMap&apos;s website), only when you tap it.
            </li>
          </ul>
        </li>
        <li>Authorities, only when the law requires it.</li>
      </ul>
      <p>We do not sell your data or share it with advertisers or data brokers.</p>

      <h2>Your controls</h2>
      <ul>
        <li>
          <strong>Pause sharing</strong> for every circle (in the Android or iPhone app or your account page) or for one
          circle. While paused, nobody in those circles can see your location, battery, place or history. After you
          resume they still never see the locations, arrivals and departures, check-ins, drives or location messages
          from while it was paused, also when a guardian paused it for you. A global pause also stops new locations
          being stored. Sending an SOS overrides a pause until the alert ends (see above).
        </li>
        <li>
          <strong>Share an approximate area</strong> with every circle or one circle (in the Android app, your account
          page or the circle&apos;s page) for 1, 3 or 6 hours or until you turn it off (see above).
        </li>
        <li>
          <strong>Supervision</strong>: accept or decline an offer on the circle&apos;s page, see your supervision, its
          activity and your requests there and on your account page, ask a guardian when a control asks first, and stop
          supervision on your own (it ends after the notice period; you can keep it after all until then).
        </li>
        <li>
          <strong>Turn off background location</strong> in Android settings at any time; sharing then works only while
          the app is running. On an iPhone, choose &quot;While Using the App&quot; or &quot;Never&quot; in the
          iPhone&apos;s Settings → ten20 → Location; sharing then works only while ten20 is on the screen, or not at
          all.
        </li>
        <li>
          <strong>In chats</strong>, unsend your own messages at any time (edit them within 15 minutes), mute a chat or
          get notified only of mentions, clear a chat&apos;s history for yourself, turn read receipts and typing off on
          your account page, and block or report people (see the <Link to="/terms">chat terms</Link>). Blocking does not
          change location sharing.
        </li>
        <li>
          <strong>Download your data</strong> from your account page: your profile, devices, circles, your arrivals and
          departures, daily summaries, SOS alerts, check-ins, emergency contacts, when you shared an approximate area,
          every retained location, the days you used ten20 and with which apps, your supervision (its controls, offers,
          your requests and the activity log, and the requests you decided as a guardian), the chats you are in, the
          messages and reactions you sent, a list of the photos, videos, files and voice clips you sent and the people
          you blocked, as JSON. It does not include other people&apos;s messages.
        </li>
        <li>
          <strong>Delete your account</strong> from your account page or the Android app&apos;s Settings. See{" "}
          <Link to="/delete-account">deleting your account</Link>. Your messages stay in other people&apos;s chats,
          shown as from &quot;Deleted account&quot;; unsend any you want gone first.
        </li>
      </ul>
      <p>
        Depending on where you live (for example under the GDPR or UK GDPR), you also have the right to access, correct,
        erase, restrict or object to processing of your data and to complain to your data protection authority. Contact
        us at <ContactLink /> and we will respond within 30 days.
      </p>

      <h2>How long we keep it</h2>
      <ul>
        <li>Location history, place arrivals and departures, and check-ins: 30 days, then deleted automatically.</li>
        <li>SOS alerts and their responses: 30 days after the alert ends, then deleted automatically.</li>
        <li>
          Records of approximate sharing (when, with which circles, the area and its centre): 30 days after it ended,
          except the latest one for each choice of circles and one still hiding your latest location, kept until
          replaced so those stay hidden.
        </li>
        <li>
          Supervision requests and the supervision activity log: as long as location history (30 days) after they were
          decided or logged. A supervision, an offer and a pending request: until it ends, expires (offers after 7 days,
          requests after 24 hours) or your account is deleted.
        </li>
        <li>
          Records of when a circle was paused: as long as location history (30 days) after the pause ended, except the
          latest one for each circle and one still hiding your latest location, a daily summary or a location message,
          kept so those stay hidden.
        </li>
        <li>Your latest location, so circles can see where you were last: until you delete your account.</li>
        <li>Daily summaries: until you delete your account (they outlast the history they were made from).</li>
        <li>
          The days you used ten20: 400 days, then deleted automatically (we update this policy before changing that), or
          when you delete your account.
        </li>
        <li>
          Usage totals and area counts: kept, also after the data they were counted from is deleted; they hold no names,
          addresses or account ids.
        </li>
        <li>
          The admin audit log (which staff member looked at or changed which account or circle, when, and what they
          searched for): kept so staff use of it can be checked, also after the account is deleted.
        </li>
        <li>
          Sign-ins: signing out or revoking a device ends its sign-in at once; the sign-in record is deleted a day after
          it would have expired. The record of each phone or browser (its name, app and Android or iOS version and push
          address) stays on your account until you delete the account, and alerts go only to devices that are still
          signed in.
        </li>
        <li>
          Messages: until the sender unsends them or they are removed (by a circle admin or by us), also after their
          sender leaves the circle or deletes their account, unless the server&apos;s operator sets messages to expire
          after a number of days (app.ten20.live does not). A chat can also have disappearing messages (either person in
          a direct chat, or a circle&apos;s admins, set 1, 7 or 90 days): messages sent while it is on are deleted like
          an unsent message that long after they were sent. Clearing a chat&apos;s history only hides it from you.
          Unsending or removing a message deletes its text, mentions, reply, reactions and attached photos, videos,
          files and voice clips at once (their stored copies within minutes); a placeholder (sender and times) stays so
          others see that a message was there, and a copy in a report stays with the report. A photo, video, file or
          voice clip you uploaded but never sent is deleted after 24 hours.
        </li>
        <li>
          Location messages: the location in one goes when you leave the circle whose chat it is in, or when you delete
          your account (direct chats), like a check-in; your current location and live ETAs also go after 30 days, with
          your location history. The message itself stays, showing that the location was removed.
        </li>
        <li>
          Reports: until we delete them, also after either account is deleted, so repeated abuse can be handled; ask us
          if you want yours deleted.
        </li>
        <li>Your reactions, chat settings and blocks: until you change them or delete your account.</li>
        <li>
          Your profile photo: until you replace or remove it, then deleted at once (its stored copies within minutes),
          unless an open report about you kept it: then it stays until every report that kept it is closed (and is
          deleted within the hour after that). Deleting your account deletes it at once, even if a report kept it.
        </li>
        <li>
          Events, with their chat, places, invites and memberships: 30 days after the event ends, then deleted
          automatically (a day later if the warning went out late).
        </li>
        <li>Your account, circles, places, settings and emergency contacts: until you delete them or your account.</li>
        <li>Backups are kept for a limited time and then overwritten, so deleted data leaves them too.</li>
      </ul>

      <h2 id="early-access">Early access list</h2>
      <p>
        If you join the early access list on ten20.live, we store the email address you give, which phone you chose
        (Android, iPhone or both) and when you signed up, and email you a link to confirm the address. Until you
        confirm, you are not on the list, and we delete the address 7 days after the last link we sent. Once confirmed,
        we use it only to email you an invite and news about ten20&apos;s launch. It is not linked to a ten20 account
        (we only check whether the same address later created one: staff who manage the list see that for each address,
        others only how many did), and we never share it or use it for advertising. We delete the list once ten20 has
        launched and the invites have gone out, or remove you sooner if you ask: email <ContactLink /> from that
        address.
      </p>

      <h2>Security</h2>
      <p>
        All connections use HTTPS. Passwords, refresh tokens and integration tokens are stored only as hashes, and the
        Android app keeps its sign-in encrypted with a key that never leaves the Android Keystore. The iPhone app keeps
        its sign-in in the iPhone&apos;s Keychain, only on that phone (never in backups), and protects the locations
        waiting to be uploaded with iOS data protection, also left out of backups. No system is perfectly secure, so
        please use a strong password or a passkey.
      </p>

      <h2>Children</h2>
      <p>
        ten20 is for families, but it is not directed at children under 13, and accounts must be created by someone at
        least 13 (or the age of digital consent where they live). A parent or guardian who sets up ten20 on a younger
        child&apos;s phone is responsible for that account and for the child&apos;s consent.
      </p>

      <h2>Changes</h2>
      <p>
        If we change this policy we will update the date above, and tell you in the apps before any change that affects
        how your location is used.
      </p>

      <h2>Contact</h2>
      <p>
        Questions or requests about your data: <ContactLink />.
      </p>
    </LegalLayout>
  );
}
